import { Box, FlexProps, VStack } from '@chakra-ui/react';
import styled from '@emotion/styled';
import type {
  DraggableProvided,
  DraggableStateSnapshot,
  DroppableProvided,
  DroppableStateSnapshot,
} from '@hello-pangea/dnd';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import React, { CSSProperties, FC, ReactElement } from 'react';

import SortableCard from './sortable-card';
import type { SortableItem } from './types';

const scrollContainerHeight = 250;

const ScrollContainer = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: ${scrollContainerHeight}px;
`;

interface Props {
  listId?: string;
  listType?: string;
  items: SortableItem[];
  internalScroll?: boolean;
  scrollContainerStyle?: CSSProperties;
  isDropDisabled?: boolean;
  style?: CSSProperties;
  ignoreContainerClipping?: boolean; // may not be provided - and might be null
  sortableCardProps?: FlexProps;
  isPromotion?: boolean;
  handleClickIndexing?: (id: string) => void;
  pageLoadedIndex?: number[];
  showIndexingButton?: boolean;
  handleMoveToTop?: (id: string) => void;
  showMoveToTopOnLastItems?: boolean;
  totalItems?: number;
}

interface SortableItemListProps {
  items: SortableItem[];
  sortableCardProps?: FlexProps;
  isPromotion?: boolean;
  handleClickIndexing?: (id: string) => void;
  pageLoadedIndex?: number[];
  showIndexingButton?: boolean;
  isDropDisabled?: boolean;
  handleMoveToTop?: (id: string) => void;
  showMoveToTopOnLastItems?: boolean;
  totalItems?: number;
}

function InnerSortableItemList(props: SortableItemListProps): ReactElement {
  const totalItemsCount = props.totalItems ?? props.items.length;

  return (
    <>
      {props.items.map((item: SortableItem, index: number) => {
        // Calculate if this item should show the "Move to Top" button
        // Button shows when: totalItems > 8 AND item's originalIndex >= 8
        const itemOriginalIndex = item.originalIndex ?? index;
        const shouldShowMoveToTop =
          props.showMoveToTopOnLastItems &&
          totalItemsCount > 8 &&
          itemOriginalIndex >= 8;

        return (
          <Draggable key={item.id} draggableId={item.id || ''} index={index}>
            {(
              dragProvided: DraggableProvided,
              dragSnapshot: DraggableStateSnapshot
            ) => (
              <SortableCard
                key={item.id}
                item={item}
                isDragging={dragSnapshot.isDragging}
                isGroupedOver={Boolean(dragSnapshot.combineTargetFor)}
                provided={dragProvided}
                index={index}
                props={props.sortableCardProps}
                handleClickIndexing={props.handleClickIndexing}
                pageLoadedIndex={props.pageLoadedIndex}
                showIndexingButton={props.showIndexingButton}
                isDropDisabled={props.isDropDisabled}
                handleMoveToTop={props.handleMoveToTop}
                showMoveToTopButton={shouldShowMoveToTop}
              />
            )}
          </Draggable>
        );
      })}
    </>
  );
}

const InnerSortableItemListMemo = React.memo<SortableItemListProps>(
  InnerSortableItemList
);

interface InnerListProps {
  dropProvided: DroppableProvided;
  items: SortableItem[];
  sortableCardProps?: FlexProps;
  isPromotion?: boolean;
  handleClickIndexing?: (id: string) => void;
  pageLoadedIndex?: number[];
  showIndexingButton?: boolean;
  isDropDisabled?: boolean;
  handleMoveToTop?: (id: string) => void;
  showMoveToTopOnLastItems?: boolean;
  totalItems?: number;
}

const InnerList: FC<InnerListProps> = ({
  items,
  dropProvided,
  sortableCardProps,
  isPromotion,
  handleClickIndexing,
  pageLoadedIndex,
  showIndexingButton,
  isDropDisabled,
  handleMoveToTop,
  showMoveToTopOnLastItems,
  totalItems,
}) => {
  return (
    <VStack
      spacing={2}
      align="stretch"
      maxW="container.xl"
      ref={dropProvided.innerRef}
    >
      <InnerSortableItemListMemo
        items={items}
        sortableCardProps={sortableCardProps}
        isPromotion={isPromotion}
        handleClickIndexing={handleClickIndexing}
        pageLoadedIndex={pageLoadedIndex}
        showIndexingButton={showIndexingButton}
        isDropDisabled={isDropDisabled}
        handleMoveToTop={handleMoveToTop}
        showMoveToTopOnLastItems={showMoveToTopOnLastItems}
        totalItems={totalItems}
      />
      {dropProvided.placeholder}
    </VStack>
  );
};

export default function SortableItemList(props: Props): ReactElement {
  const {
    ignoreContainerClipping,
    internalScroll,
    scrollContainerStyle,
    isDropDisabled,
    listId = 'LIST',
    listType,
    items,
  } = props;

  return (
    <Droppable
      droppableId={listId}
      type={listType}
      ignoreContainerClipping={ignoreContainerClipping}
      isDropDisabled={isDropDisabled}
      isCombineEnabled={false}
    >
      {(
        dropProvided: DroppableProvided,
        dropSnapshot: DroppableStateSnapshot
      ) => (
        <Box
          backgroundColor={
            dropSnapshot.isDraggingOver ||
            Boolean(dropSnapshot.draggingFromThisWith)
              ? 'gray.50'
              : 'white'
          }
          {...dropProvided.droppableProps}
          minWidth="inherit"
          maxW="inherit"
          alignItems="center"
          gap="2"
        >
          {internalScroll ? (
            <ScrollContainer style={scrollContainerStyle}>
              <InnerList
                items={items}
                dropProvided={dropProvided}
                sortableCardProps={props.sortableCardProps}
                isPromotion={props.isPromotion}
                handleClickIndexing={props.handleClickIndexing}
                pageLoadedIndex={props.pageLoadedIndex}
                showIndexingButton={props.showIndexingButton}
                isDropDisabled={props.isDropDisabled}
                handleMoveToTop={props.handleMoveToTop}
                showMoveToTopOnLastItems={props.showMoveToTopOnLastItems}
                totalItems={props.totalItems}
              />
            </ScrollContainer>
          ) : (
            <InnerList
              items={items}
              dropProvided={dropProvided}
              sortableCardProps={props.sortableCardProps}
              isPromotion={props.isPromotion}
              handleClickIndexing={props.handleClickIndexing}
              pageLoadedIndex={props.pageLoadedIndex}
              showIndexingButton={props.showIndexingButton}
              isDropDisabled={props.isDropDisabled}
              handleMoveToTop={props.handleMoveToTop}
              showMoveToTopOnLastItems={props.showMoveToTopOnLastItems}
              totalItems={props.totalItems}
            />
          )}
        </Box>
      )}
    </Droppable>
  );
}
